/* eslint-disable react/display-name */

import { PrismicRichText } from '@prismicio/react';
import { sharedLinkResolver } from 'remote-com-shared/domains/navigation/hrefResolver';
import { pixelToRem } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { LinkButton } from '@/src/components/Button';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const BannerWrapper = styled.div`
  -webkit-text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  --vh: 4.34px;
  font-family: 'Inter', sans-serif;
  --navigationLineColor: #fff;
  --linkColor: rgba(255, 255, 255, 0.7);
  --linkHoverColor: rgba(255, 255, 255, 1);
  --buttonColor: #fff;
  --buttonPrimaryBackground: #ff4a5a;
  --buttonSecondaryBackground: #624de3;
  box-sizing: inherit;
  display: flex;
  height: max-content;
  min-height: 70px;
  align-items: center;
  justify-content: center;
  z-index: 10;
  font-weight: 500;
  color: #efedfc;
  background-image: linear-gradient(227.4deg, #624de3 -3.02%, #4737a8 79.84%);
  background-color: #4737a8;
  font-size: 16px;
  line-height: 1.375;
  flex-wrap: unset;
  padding: 22px;

  & > a {
    color: #efedfc;
    height: 24px;
  }

  & > a > span {
    display: flex;
    width: 100%;
    justify-content: center;

    img {
      height: 24px;
      margin-right: 8px;
    }

    > p > span {
      color: var(--colors-turquoise);
      font-weight: 600;
    }
  }

  * + *:not(strong) {
    margin-left: 8px;
  }

  img + p > strong:nth-of-type(1) {
    margin-right: 8px;
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
    /* white-space: nowrap; */
  }

  > a > p {
    display: inline-flex;
    align-items: center;
    font-weight: 400;
    color: inherit;
    margin: 0;
  }

  & > a > p::after {
    content: '';
    background-image: url(/images/chevron-right-turquoise.svg);
    background-repeat: no-repeat;
    width: 20px;
    height: 13px;
    margin-left: 7px;
  }

  ${tabletBreakpoint(css`
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: 5px;
    padding: 15px;

    & > * {
      font-size: ${pixelToRem('14px')};
    }

    & > p {
      word-break: break-word;
    }
  `)}
`;

export const BannerHighlightedStrong = styled.strong`
  font-weight: 600;
  color: var(--colors-turquoise);

  * {
    color: var(--colors-turquoise);
  }
`;

const BannerLinkText = styled(LinkButton).attrs({
  chevron: true,
})`
  color: var(--colors-turquoise);
  font-weight: inherit;
  && > svg {
    margin-left: 0;
  }
`;

export const BannerIcon = styled.img`
  max-height: 24px;
  max-width: 24px;

  ${tabletBreakpoint(css`
    grid-row: 1/2;
  `)}
`;

export const BannerCTA = styled(LinkButton).attrs({
  chevron: true,
})`
  ${tabletBreakpoint(css`
    grid-row: 2;
    grid-column: 2;
    justify-self: end;
  `)}

  && {
    margin-left: 36px;
    color: var(--colors-selago);
    font-weight: 400;

    & > svg {
      margin-left: 0;
    }
  }
`;

export const PrismicCustomComponents = {
  strong: (props) => <BannerHighlightedStrong {...props} />,
  hyperlink: ({ children, ...props }) => {
    const {
      node: { data },
    } = props;

    switch (data?.link_type) {
      case 'Document': {
        const { target } = data;

        return (
          <BannerLinkText
            label={children}
            target={target}
            href={sharedLinkResolver(props.node?.data)}
          />
        );
      }
      case 'Web': {
        const { url, target } = data;
        return <BannerLinkText label={children} href={url} target={target} />;
      }
      default:
        return null;
    }
  },
};

export const Banner = ({ icon, description, callToAction, callToActionLink }) => (
  <>
    <BannerWrapper>
      {icon ? <BannerIcon src={icon.url} alt=" " /> : <span />}
      <PrismicRichText field={description} components={PrismicCustomComponents} />
      {callToAction && callToActionLink?.url && (
        <BannerCTA label={callToAction} href={callToActionLink.url} target="_blank" />
      )}
    </BannerWrapper>
  </>
);
